import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import useAdminAuth from '../hooks/useAdminAuth';
import { MessageSquare, Check, X, MapPin, User, Clock, Reply, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateAU } from '../utils/dateFormat';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminQA = () => {
  const navigate = useNavigate();
  useAdminAuth();
  
  const [pendingQuestions, setPendingQuestions] = useState([]);
  const [pendingReplies, setPendingReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Rejection modal state
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [itemToReject, setItemToReject] = useState(null);
  const [rejectType, setRejectType] = useState(''); // 'question' or 'reply'
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const adminEmail = localStorage.getItem('admin_email') || 'info@trucknsendit.com';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/admin/questions/pending?admin_email=${adminEmail}`);
      setPendingQuestions(response.data.pending_questions || []);
      setPendingReplies(response.data.pending_replies || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveQuestion = async (questionId) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/api/admin/questions/${questionId}/approve?admin_email=${adminEmail}`);
      toast.success('Question approved and shipper notified');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve question');
    } finally {
      setProcessing(false);
    }
  };

  const handleApproveReply = async (replyId) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/api/admin/replies/${replyId}/approve?admin_email=${adminEmail}`);
      toast.success('Reply approved and asker notified');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve reply');
    } finally {
      setProcessing(false);
    }
  };

  const openRejectModal = (item, type) => {
    setItemToReject(item);
    setRejectType(type);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    if (!itemToReject) return;
    
    setProcessing(true);
    try {
      const endpoint = rejectType === 'question' 
        ? `/api/admin/questions/${itemToReject.id}/reject`
        : `/api/admin/replies/${itemToReject.id}/reject`;
      
      await axios.post(`${API}${endpoint}?admin_email=${adminEmail}&reason=${encodeURIComponent(rejectionReason)}`);
      
      toast.success(`${rejectType === 'question' ? 'Question' : 'Reply'} rejected`);
      setShowRejectModal(false);
      setItemToReject(null);
      setRejectionReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessing(false);
    }
  };

  const QuestionCard = ({ question }) => (
    <Card className="p-4 mb-4 border-l-4 border-l-yellow-500">
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-yellow-600" />
          <span className="font-semibold text-[#1e3a5f]">New Question</span>
        </div>
        <span className="text-xs text-gray-500 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {formatDateAU(question.created_at, { includeTime: true })}
        </span>
      </div>
      
      {/* Load Info */}
      {question.load_info && (
        <div className="bg-gray-50 p-2 rounded mb-3 text-sm">
          <div className="flex items-center gap-2 text-gray-600">
            <MapPin className="w-4 h-4" />
            <span>{question.load_info.pickup_location} → {question.load_info.dropoff_location}</span>
          </div>
          <p className="text-gray-600 mt-1">
            <strong>Load:</strong> {question.load_info.cargo_type} | 
            <strong> Shipper:</strong> {question.load_info.shipper_email}
          </p>
        </div>
      )}
      
      {/* Asker Info */}
      <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
        <User className="w-4 h-4" />
        <span><strong>From:</strong> {question.asker_name} ({question.asker_email})</span>
        <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">{question.asker_role}</span>
      </div>
      
      {/* Question Text */}
      <div className="bg-yellow-50 p-3 rounded mb-4 border border-yellow-200">
        <p className="text-gray-800">{question.question_text}</p>
      </div>
      
      {/* Actions */}
      <div className="flex gap-2">
        <Button
          onClick={() => handleApproveQuestion(question.id)}
          disabled={processing}
          className="bg-green-600 hover:bg-green-700"
          size="sm"
          data-testid={`approve-question-${question.id}`}
        >
          <Check className="w-4 h-4 mr-1" />
          Approve
        </Button>
        <Button
          onClick={() => openRejectModal(question, 'question')}
          variant="outline"
          className="text-red-600 border-red-300 hover:bg-red-50"
          size="sm"
          data-testid={`reject-question-${question.id}`}
        >
          <X className="w-4 h-4 mr-1" />
          Reject
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/load/${question.load_id}`)}
        >
          View Load
        </Button>
      </div>
    </Card>
  );

  const ReplyCard = ({ reply }) => (
    <Card className="p-4 mb-4 border-l-4 border-l-blue-500">
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-2">
          <Reply className="w-5 h-5 text-blue-600" />
          <span className="font-semibold text-[#1e3a5f]">New Reply from Shipper</span>
        </div>
        <span className="text-xs text-gray-500 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {formatDateAU(reply.created_at, { includeTime: true })}
        </span>
      </div>
      
      {/* Original Question */}
      <div className="bg-gray-50 p-2 rounded mb-3 text-sm">
        <p className="text-gray-600">
          <strong>Original question from {reply.asker_name}:</strong>
        </p>
        <p className="text-gray-700 italic">"{reply.question_text}"</p>
      </div>
      
      {/* Replier Info */}
      <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
        <User className="w-4 h-4" />
        <span><strong>Reply from:</strong> {reply.replier_name} ({reply.replier_email})</span>
        <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs">shipper</span>
      </div>
      
      {/* Reply Text */}
      <div className="bg-blue-50 p-3 rounded mb-4 border border-blue-200">
        <p className="text-gray-800">{reply.reply_text}</p>
      </div>
      
      {/* Actions */}
      <div className="flex gap-2">
        <Button
          onClick={() => handleApproveReply(reply.id)}
          disabled={processing}
          className="bg-green-600 hover:bg-green-700"
          size="sm"
          data-testid={`approve-reply-${reply.id}`}
        >
          <Check className="w-4 h-4 mr-1" />
          Approve
        </Button>
        <Button
          onClick={() => openRejectModal(reply, 'reply')}
          variant="outline"
          className="text-red-600 border-red-300 hover:bg-red-50"
          size="sm"
          data-testid={`reject-reply-${reply.id}`}
        >
          <X className="w-4 h-4 mr-1" />
          Reject
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/load/${reply.load_id}`)}
        >
          View Load
        </Button>
      </div>
    </Card>
  );

  const totalPending = pendingQuestions.length + pendingReplies.length;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-[#1e3a5f] flex items-center gap-2">
              <MessageSquare className="w-8 h-8" />
              Q&A Moderation
            </h1>
            <p className="text-gray-600 mt-1">
              Review and approve questions and replies before they're visible
            </p>
          </div>
          
          <Button onClick={fetchData} variant="outline">
            Refresh
          </Button>
        </div>

        {/* Summary */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <Card className="p-4 bg-yellow-50 border-yellow-200">
            <div className="flex items-center gap-3">
              <MessageSquare className="w-8 h-8 text-yellow-600" />
              <div>
                <p className="text-2xl font-bold text-yellow-800">{pendingQuestions.length}</p>
                <p className="text-sm text-yellow-600">Pending Questions</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 bg-blue-50 border-blue-200">
            <div className="flex items-center gap-3">
              <Reply className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold text-blue-800">{pendingReplies.length}</p>
                <p className="text-sm text-blue-600">Pending Replies</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 bg-gray-50 border-gray-200">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-8 h-8 text-gray-600" />
              <div>
                <p className="text-2xl font-bold text-gray-800">{totalPending}</p>
                <p className="text-sm text-gray-600">Total Pending</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="all" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="all">All Pending ({totalPending})</TabsTrigger>
            <TabsTrigger value="questions">Questions ({pendingQuestions.length})</TabsTrigger>
            <TabsTrigger value="replies">Replies ({pendingReplies.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="all">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p>Loading...</p>
              </div>
            ) : totalPending === 0 ? (
              <Card className="p-12 text-center">
                <Check className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">All Caught Up!</h3>
                <p className="text-gray-500">No pending questions or replies to review.</p>
              </Card>
            ) : (
              <div>
                {pendingQuestions.map(question => (
                  <QuestionCard key={question.id} question={question} />
                ))}
                {pendingReplies.map(reply => (
                  <ReplyCard key={reply.id} reply={reply} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="questions">
            {pendingQuestions.length === 0 ? (
              <Card className="p-12 text-center">
                <Check className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600">No Pending Questions</h3>
              </Card>
            ) : (
              pendingQuestions.map(question => (
                <QuestionCard key={question.id} question={question} />
              ))
            )}
          </TabsContent>

          <TabsContent value="replies">
            {pendingReplies.length === 0 ? (
              <Card className="p-12 text-center">
                <Check className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600">No Pending Replies</h3>
              </Card>
            ) : (
              pendingReplies.map(reply => (
                <ReplyCard key={reply.id} reply={reply} />
              ))
            )}
          </TabsContent>
        </Tabs>

        {/* Rejection Modal */}
        {showRejectModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md p-6">
              <h2 className="text-xl font-bold text-[#1e3a5f] mb-4 flex items-center gap-2">
                <X className="w-5 h-5 text-red-600" />
                Reject {rejectType === 'question' ? 'Question' : 'Reply'}
              </h2>
              
              <p className="text-gray-600 mb-4">
                Please provide a reason for rejection. This will be sent to the {rejectType === 'question' ? 'person who asked' : 'shipper'}.
              </p>
              
              {itemToReject && (
                <div className="bg-gray-50 p-3 rounded mb-4 text-sm">
                  <p className="text-gray-600">
                    <strong>Content:</strong>
                  </p>
                  <p className="text-gray-700 italic">
                    "{rejectType === 'question' ? itemToReject.question_text : itemToReject.reply_text}"
                  </p>
                </div>
              )}
              
              <div className="mb-4">
                <Label>Rejection Reason *</Label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="e.g., This question contains inappropriate language, Please rephrase your question to be more specific..."
                  rows={3}
                />
                <p className="text-xs text-gray-500 mt-1">
                  This message will be sent to the sender via email.
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={handleReject}
                  disabled={processing || !rejectionReason.trim()}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  {processing ? 'Rejecting...' : 'Reject & Notify'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowRejectModal(false);
                    setItemToReject(null);
                    setRejectionReason('');
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </Card>
          </div>
        )}
      </main>
      
      <Footer />
    </div>
  );
};

export default AdminQA;
